import React, { useState, useEffect } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Constant from "../Constant";
import { getAccessToken } from "../../hooks/AccessToken";
import Axios from "axios";
import { error } from "jquery";

const EditResult = ({
  open,
  onClose,
  contestId,
  resultArray,
  judgeResult,
  onResultUpdated,
  showNotification,
  totalNumberOfWinners,
  isLanguageBased,
  languages,
  winnersPerLanguage,
  resultTitle = "",
  judgeResultTitle = "",
  displayResult = 0,
  resultImage = null,
}) => {
  const [activeTab, setActiveTab] = useState("auto");
  const [autoWinnersList, setAutoWinnersList] = useState([]);
  const [autoLanguageWinners, setAutoLanguageWinners] = useState({});
  const [judgeWinnersList, setJudgeWinnersList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedWinnerIndex, setSelectedWinnerIndex] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [languageMap, setLanguageMap] = useState({});
  const [currentResultTitle, setCurrentResultTitle] = useState(resultTitle);
  const [currentJudgeResultTitle, setCurrentJudgeResultTitle] =
    useState(judgeResultTitle);
  // Checkbox states for displayResult
  // displayResult: 0 = both, 1 = judge only, 2 = auto only
  const [displayAuto, setDisplayAuto] = useState(true);
  const [displayJudge, setDisplayJudge] = useState(true);
  // Notification state for displaying messages inside the modal
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("error"); // "error" or "success"
  // Result image state
  const [currentResultImage, setCurrentResultImage] = useState(resultImage);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const resultImageInputRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      fetchWinnersDetails();
      fetchJudgeWinnersDetails();
      fetchLanguages();
      setActiveTab("auto");
      setCurrentResultTitle(resultTitle);
      setCurrentJudgeResultTitle(judgeResultTitle);
      setCurrentResultImage(resultImage);
      setImageUploadError("");
      // Clear any previous notifications
      setNotificationMessage("");
      // Initialize checkboxes based on displayResult
      // 0 = both checked, 1 = only judge, 2 = only auto
      if (displayResult === 0) {
        setDisplayAuto(true);
        setDisplayJudge(true);
      } else if (displayResult === 1) {
        setDisplayAuto(false);
        setDisplayJudge(true);
      } else if (displayResult === 2) {
        setDisplayAuto(true);
        setDisplayJudge(false);
      } else {
        // Default to both checked
        setDisplayAuto(true);
        setDisplayJudge(true);
      }
    } else {
      // Clear notification when modal closes
      setNotificationMessage("");
    }
  }, [
    open,
    resultArray,
    judgeResult,
    resultTitle,
    judgeResultTitle,
    displayResult,
    resultImage,
  ]);

  const fetchLanguages = async () => {
    try {
      const token = getAccessToken();
      const response = await Axios.get(
        Constant.apiBasePath + "language/get-all",
        {
          headers: { token: token },
        }
      );
      if (response.data) {
        const langMap = {};
        response.data.forEach((lang) => {
          langMap[lang.name] = {
            name: lang.name,
          };
        });
        setLanguageMap(langMap);
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  const fetchUserName = async (id) => {
    try {
      const response = await Axios.get(
        `https://api.storytent.in/api/user/getUserDetails/${id}`
      );

      if (response.status === 200) {
        console.log("User name fetched successfully:", response.data);
      }

      const result = response.data;
      return result.data.name;
    } catch (error) {
      console.error("Error fetching user name:", error);
    }
  };

  const fetchWinnersDetails = async () => {
    setLoading(true);
    try {
      if (isLanguageBased) {
        const initialLanguageWinners = {};
        languages.forEach((lang) => {
          initialLanguageWinners[lang] = [];
        });
        if (resultArray && typeof resultArray === "object") {
          for (const [lang, winners] of Object.entries(resultArray)) {
            if (initialLanguageWinners.hasOwnProperty(lang)) {
              const winnersPromises = winners.map(async (winner, index) => {
                if (winner.userId) {
                  try {
                    const name = await fetchUserName(winner.userId);
                    return {
                      userId: winner.userId,
                      name: name || "Unknown Name",
                      position: index + 1,
                      tag: winner.tag || "",
                      prizeMoney: winner.prizeMoney || 0,
                    };
                  } catch (error) {
                    return {
                      userId: winner.userId,
                      name: "Error Fetching Name",
                      position: index + 1,
                      tag: winner.tag || "",
                      prizeMoney: winner.prizeMoney || 0,
                    };
                  }
                } else {
                  return {
                    userId: null,
                    name: winner.name || "",
                    position: index + 1,
                    tag: winner.tag || "",
                    prizeMoney: winner.prizeMoney || 0,
                  };
                }
              });
              const resolvedWinners = await Promise.all(winnersPromises);
              initialLanguageWinners[lang] = resolvedWinners;
            }
          }
        }
        languages.forEach((lang) => {
          const currentWinners = initialLanguageWinners[lang] || [];
          const remainingPositions = winnersPerLanguage - currentWinners.length;
          for (let i = 0; i < remainingPositions; i++) {
            currentWinners.push({
              userId: null,
              name: "Empty Position",
              position: currentWinners.length + 1,
              tag: "",
              prizeMoney: 0,
            });
          }
          initialLanguageWinners[lang] = currentWinners;
        });
        setAutoLanguageWinners(initialLanguageWinners);
      } else {
        const initialWinnersList = Array(totalNumberOfWinners)
          .fill(null)
          .map((_, index) => ({
            userId: null,
            name: "Empty Position",
            position: index + 1,
          }));
        if (resultArray && Array.isArray(resultArray)) {
          const winnersPromises = resultArray.map(async (userId, index) => {
            if (userId) {
              try {
                const name = await fetchUserName(userId);
                return {
                  userId: userId,
                  name: name || "Unknown Name",
                  position: index + 1,
                };
              } catch (error) {
                return {
                  userId: userId,
                  name: "Error Fetching Name",
                  position: index + 1,
                };
              }
            }
            return {
              userId: null,
              name: "Empty Position",
              position: index + 1,
            };
          });
          const resolvedWinners = await Promise.all(winnersPromises);
          resolvedWinners.forEach((winner, index) => {
            if (index < initialWinnersList.length) {
              initialWinnersList[index] = winner;
            }
          });
        }
        setAutoWinnersList(initialWinnersList);
      }
    } catch (error) {
      showNotification("Failed to load winners details", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchJudgeWinnersDetails = async () => {
    setLoading(true);
    try {
      // judgeResult is always a single array, not language-based
      const initialWinnersList = [];

      if (judgeResult && Array.isArray(judgeResult)) {
        const winnersPromises = judgeResult.map(async (winner, index) => {
          if (winner.userId) {
            try {
              const name = await fetchUserName(winner.userId);
              return {
                userId: winner.userId,
                name: name || "Unknown Name",
                position: index + 1,
                tag: winner.tag || "",
                prizeMoney: winner.prizeMoney || 0,
              };
            } catch (error) {
              return {
                userId: winner.userId,
                name: "Error Fetching Name",
                position: index + 1,
                tag: winner.tag || "",
                prizeMoney: winner.prizeMoney || 0,
              };
            }
          } else {
            return {
              userId: null,
              name: winner.name || "",
              position: index + 1,
              tag: winner.tag || "",
              prizeMoney: winner.prizeMoney || 0,
            };
          }
        });
        const resolvedWinners = await Promise.all(winnersPromises);
        initialWinnersList.push(...resolvedWinners);
      }

      // If no existing winners, add one empty row to start with
      if (initialWinnersList.length === 0) {
        initialWinnersList.push({
          userId: null,
          name: "",
          position: 1,
          tag: "",
          prizeMoney: 0,
        });
      }

      setJudgeWinnersList(initialWinnersList);
    } catch (error) {
      showNotification("Failed to load judge winners details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    try {
      const token = getAccessToken();
      const response = await Axios.get(
        `https://api.storytent.in/api/user/searchUsersByName?name=${searchTerm}`,
        {
          headers: {
            token: token,
          },
        }
      );
      const result = response;

      if (result.status === 200 && result.data) {
        console.log("Search results:", result.data.data);
        const formattedResults = result.data.data.map((participant) => ({
          id: participant._id,
          name: participant.name,
          profilePic: participant.profilePic,
        }));

        setSearchResults(formattedResults);
      } else {
        showNotification("Failed to search participants", "error");
      }
    } catch (error) {
      showNotification("Failed to search users", "error");
      console.error("Error searching users:", error);
    }
  };

  const selectWinner = (userId, userName) => {
    if (selectedWinnerIndex !== null) {
      if (activeTab === "auto" && isLanguageBased && selectedLanguage) {
        const updatedLanguageWinners = { ...languageWinners };
        const existingWinner =
          updatedLanguageWinners[selectedLanguage][selectedWinnerIndex];
        updatedLanguageWinners[selectedLanguage][selectedWinnerIndex] = {
          ...existingWinner,
          userId: userId,
          name: userName,
        };
        setLanguageWinners(updatedLanguageWinners);
      } else if (activeTab === "auto") {
        const updatedWinnersList = [...winnersList];
        updatedWinnersList[selectedWinnerIndex] = {
          ...updatedWinnersList[selectedWinnerIndex],
          userId: userId,
          name: userName,
        };
        setWinnersList(updatedWinnersList);
      } else {
        // judgeResult tab - handle unlimited winners
        const updatedWinnersList = [...winnersList];
        updatedWinnersList[selectedWinnerIndex] = {
          ...updatedWinnersList[selectedWinnerIndex],
          userId: userId,
          name: userName,
        };
        setWinnersList(updatedWinnersList);
      }
      setSelectedWinnerIndex(null);
      setSelectedLanguage(null);
      setIsSearchActive(false);
      setSearchTerm("");
      setSearchResults([]);
    }
  };

  const handleEditClick = (index, language = null) => {
    setSelectedWinnerIndex(index);
    setSelectedLanguage(language);
    setIsSearchActive(true);
    setTimeout(() => {
      const searchInput = document.getElementById("result-search-input");
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  const handleAddWinner = (index, language = null) => {
    setSelectedWinnerIndex(index);
    setSelectedLanguage(language);
    setIsSearchActive(true);
    setTimeout(() => {
      const searchInput = document.getElementById("result-search-input");
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  const showModalNotification = (message, type = "error") => {
    setNotificationMessage(message);
    setNotificationType(type);
    // Also call the parent notification for consistency
    if (showNotification) {
      showNotification(message, type);
    }
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setNotificationMessage("");
    }, 3000);
  };

  const handleResultImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Validate type
    if (!file.type.startsWith("image/")) {
      setImageUploadError("Please select a valid image file.");
      return;
    }
    setIsImageUploading(true);
    setImageUploadError("");
    try {
      const token = getAccessToken();
      const formData = new FormData();
      formData.append("resultImage", file);
      const response = await Axios.patch(
        `${Constant.apiBasePath}contest/upload-result-image/${contestId}`,
        formData,
        { headers: { token, "Content-Type": "multipart/form-data" } }
      );
      if (response.data && response.data.data && response.data.data.resultImage) {
        setCurrentResultImage(response.data.data.resultImage);
        showModalNotification("Result image uploaded successfully.", "success");
        onResultUpdated && onResultUpdated();
      }
    } catch (err) {
      setImageUploadError("Upload failed. Please try again.");
      console.error("Result image upload error:", err);
    } finally {
      setIsImageUploading(false);
      // Reset the file input so the same file can be re-selected
      if (resultImageInputRef.current) resultImageInputRef.current.value = "";
    }
  };

  const handleResultImageRemove = async () => {
    if (!window.confirm("Remove the result image? The dynamic winner cards will be shown instead.")) return;
    setIsImageUploading(true);
    setImageUploadError("");
    try {
      const token = getAccessToken();
      await Axios.delete(
        `${Constant.apiBasePath}contest/remove-result-image/${contestId}`,
        { headers: { token } }
      );
      setCurrentResultImage(null);
      showModalNotification("Result image removed. Dynamic cards will now display.", "success");
      onResultUpdated && onResultUpdated();
    } catch (err) {
      setImageUploadError("Remove failed. Please try again.");
      console.error("Result image remove error:", err);
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleDisplayAutoChange = (checked) => {
    // Validation: At least one checkbox must be checked
    if (!checked && !displayJudge) {
      showModalNotification("Atleast 1 result must be displayed.", "error");
      return;
    }
    setDisplayAuto(checked);
  };

  const handleDisplayJudgeChange = (checked) => {
    // Validation: At least one checkbox must be checked
    if (!checked && !displayAuto) {
      showModalNotification("Atleast 1 result must be displayed.", "error");
      return;
    }
    setDisplayJudge(checked);
  };

  const calculateDisplayResult = () => {
    // 0 = both checked, 1 = only judge, 2 = only auto
    if (displayAuto && displayJudge) {
      return 0;
    } else if (displayJudge && !displayAuto) {
      return 1;
    } else if (displayAuto && !displayJudge) {
      return 2;
    } else {
      // Fallback (should not happen due to validation)
      return 0;
    }
  };

  const handleSubmit = async () => {
    // Validation: At least one checkbox must be checked
    if (!displayAuto && !displayJudge) {
      showNotification("Atleast 1 result must be displayed.", "error");
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    try {
      let updatedResultArray, updatedJudgeResult;
      const calculatedDisplayResult = calculateDisplayResult();
      let payload = {
        resultTitle: currentResultTitle,
        judgeResultTitle: currentJudgeResultTitle,
        displayResult: calculatedDisplayResult,
      };
      if (activeTab === "auto") {
        if (isLanguageBased) {
          updatedResultArray = {};
          Object.entries(languageWinners).forEach(([lang, winners]) => {
            updatedResultArray[lang] = winners
              .filter(
                (winner) =>
                  winner.userId !== null ||
                  winner.tag ||
                  winner.prizeMoney > 0 ||
                  (winner.name && winner.name !== "Empty Position")
              )
              .map((winner) => ({
                userId: winner.userId,
                name: winner.name,
                tag: winner.tag,
                prizeMoney: winner.prizeMoney,
              }));
          });
        } else {
          updatedResultArray = winnersList
            .filter((winner) => winner.userId !== null)
            .map((winner) => winner.userId);
        }
        payload.resultArray = updatedResultArray;
      } else {
        updatedJudgeResult = winnersList.map((winner) => ({
          userId: winner.userId,
          name: winner.name,
          tag: winner.tag || "",
          prizeMoney: winner.prizeMoney || 0,
        }));
        payload.judgeResult = updatedJudgeResult;
      }
      const response = await Axios.patch(
        `${Constant.apiBasePath}contest/update-result/${contestId}`,
        payload,
        {
          headers: {
            token: getAccessToken(),
          },
        }
      );
      if (response.status === 200 || response.status === 201) {
        showNotification(
          activeTab === "auto"
            ? "Contest results updated successfully"
            : "Judge results updated successfully"
        );
        if (activeTab === "auto") {
          onResultUpdated(payload.resultArray);
        } else {
          onResultUpdated(payload.judgeResult);
        }
      }
      onClose();
    } catch (error) {
      if (error.response && error.response.data) {
        showNotification(error.response.data.message, "error");
      } else {
        showNotification(
          activeTab === "auto"
            ? "Failed to update contest results"
            : "Failed to update judge results",
          "error"
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearPosition = (index, language = null) => {
    if (activeTab === "auto" && isLanguageBased && language) {
      const updatedLanguageWinners = { ...languageWinners };
      updatedLanguageWinners[language][index] = {
        ...updatedLanguageWinners[language][index],
        userId: null,
        name: "Empty Position",
      };
      setLanguageWinners(updatedLanguageWinners);
    } else if (activeTab === "auto") {
      const updatedWinnersList = [...winnersList];
      updatedWinnersList[index] = {
        ...updatedWinnersList[index],
        userId: null,
        name: "Empty Position",
      };
      setWinnersList(updatedWinnersList);
    } else {
      // judgeResult tab - remove winner and shift positions
      const updatedWinnersList = [...winnersList];
      updatedWinnersList.splice(index, 1);

      // Update positions
      updatedWinnersList.forEach((winner, i) => {
        winner.position = i + 1;
      });

      // If no rows left, add one empty row
      if (updatedWinnersList.length === 0) {
        updatedWinnersList.push({
          userId: null,
          name: "",
          position: 1,
          tag: "",
          prizeMoney: 0,
        });
      }

      setWinnersList(updatedWinnersList);
    }
  };

  const handleResetAll = () => {
    if (activeTab === "auto") {
      if (isLanguageBased) {
        const resetLanguageWinners = {};
        languages.forEach((lang) => {
          resetLanguageWinners[lang] = Array(winnersPerLanguage)
            .fill(null)
            .map((_, index) => ({
              userId: null,
              name: "Empty Position",
              position: index + 1,
              tag: "",
              prizeMoney: 0,
            }));
        });
        setLanguageWinners(resetLanguageWinners);
      } else {
        const resetWinnersList = Array(totalNumberOfWinners)
          .fill(null)
          .map((_, index) => ({
            userId: null,
            name: "Empty Position",
            position: index + 1,
          }));
        setWinnersList(resetWinnersList);
      }
    } else {
      setWinnersList([
        {
          userId: null,
          name: "",
          position: 1,
          tag: "",
          prizeMoney: 0,
        },
      ]);
    }
  };

  const handleTagChange = (language, index, value) => {
    if (activeTab === "auto" && isLanguageBased) {
      const updatedLanguageWinners = { ...languageWinners };
      updatedLanguageWinners[language][index] = {
        ...updatedLanguageWinners[language][index],
        tag: value,
      };
      setLanguageWinners(updatedLanguageWinners);
    } else if (activeTab === "judge") {
      const updatedWinnersList = [...winnersList];
      updatedWinnersList[index] = {
        ...updatedWinnersList[index],
        tag: value,
      };
      setWinnersList(updatedWinnersList);
    }
  };

  const handlePrizeMoneyChange = (language, index, value) => {
    if (activeTab === "auto" && isLanguageBased) {
      const updatedLanguageWinners = { ...languageWinners };
      updatedLanguageWinners[language][index] = {
        ...updatedLanguageWinners[language][index],
        prizeMoney: value === "" ? "" : Number(value),
      };
      setLanguageWinners(updatedLanguageWinners);
    } else if (activeTab === "judge") {
      const updatedWinnersList = [...winnersList];
      updatedWinnersList[index] = {
        ...updatedWinnersList[index],
        prizeMoney: value === "" ? "" : Number(value),
      };
      setWinnersList(updatedWinnersList);
    }
  };

  const handleNameChange = (language, index, value) => {
    if (activeTab === "auto" && isLanguageBased) {
      const updatedLanguageWinners = { ...languageWinners };
      updatedLanguageWinners[language][index] = {
        ...updatedLanguageWinners[language][index],
        name: value,
      };
      setLanguageWinners(updatedLanguageWinners);
    } else if (activeTab === "judge") {
      const updatedWinnersList = [...winnersList];
      updatedWinnersList[index] = {
        ...updatedWinnersList[index],
        name: value,
      };
      setWinnersList(updatedWinnersList);
    }
  };

  const handleAddJudgeRow = () => {
    const updatedWinnersList = [...winnersList];
    updatedWinnersList.push({
      userId: null,
      name: "",
      position: updatedWinnersList.length + 1,
      tag: "",
      prizeMoney: 0,
    });
    setWinnersList(updatedWinnersList);
  };

  const getTabData = () => {
    if (activeTab === "auto") {
      return {
        winnersList: autoWinnersList,
        setWinnersList: setAutoWinnersList,
        languageWinners: autoLanguageWinners,
        setLanguageWinners: setAutoLanguageWinners,
      };
    } else {
      return {
        winnersList: judgeWinnersList,
        setWinnersList: setJudgeWinnersList,
        languageWinners: {},
        setLanguageWinners: () => {},
      };
    }
  };

  const { winnersList, setWinnersList, languageWinners, setLanguageWinners } =
    getTabData();

  const modalStyle = {
    modalContent: {
      padding: "20px",
      maxWidth: "800px",
      maxHeight: "80vh",
      overflow: "auto",
    },
    searchContainer: {
      marginBottom: "20px",
    },
    searchForm: {
      display: "flex",
      marginBottom: "10px",
    },
    searchInput: {
      flex: 1,
      padding: "8px 10px",
      border: "1px solid #d5d9e8",
      borderRadius: "8px",
      marginRight: "10px",
      fontSize: "13px",
    },
    searchButton: {
      padding: "8px 14px",
      borderRadius: "8px",
      backgroundColor: "#4f46e5",
      color: "white",
      border: "none",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "700",
    },
    searchResults: {
      border: "1px solid #ddd",
      borderRadius: "4px",
      maxHeight: "150px",
      overflowY: "auto",
      padding: "10px",
      marginTop: "10px",
      display: isSearchActive ? "block" : "none",
    },
    searchResultItem: {
      padding: "8px 10px",
      cursor: "pointer",
      borderBottom: "1px solid #eee",
      fontSize: "12px",
    },
    winnersList: {
      marginBottom: "20px",
    },
    winnerItem: {
      display: "flex",
      alignItems: "center",
      padding: "10px",
      borderBottom: "1px solid #eee",
    },
    position: {
      width: "50px",
      fontWeight: "bold",
    },
    winnerName: {
      flex: 1,
    },
    editButton: {
      backgroundColor: "#f8f9fe",
      border: "1px solid #d5d9e8",
      borderRadius: "8px",
      padding: "6px 10px",
      cursor: "pointer",
      marginLeft: "10px",
    },
    addButton: {
      backgroundColor: "#4f46e5",
      color: "white",
      border: "none",
      borderRadius: "8px",
      padding: "6px 11px",
      cursor: "pointer",
      marginLeft: "10px",
      fontSize: "12px",
      fontWeight: "700",
    },
    buttonGroup: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "10px",
      marginTop: "20px",
    },
    button: {
      padding: "8px 14px",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "700",
    },
    applyButton: {
      backgroundColor: "#4f46e5",
      color: "white",
      border: "none",
    },
    cancelButton: {
      backgroundColor: "#f8f9fe",
      border: "1px solid #d5d9e8",
    },
    languageSection: {
      marginBottom: "30px",
      padding: "15px",
      border: "1px solid #ddd",
      borderRadius: "4px",
    },
    languageTitle: {
      fontSize: "1.2em",
      fontWeight: "bold",
      marginBottom: "15px",
    },
    inputField: {
      padding: "6px 8px",
      margin: "0 5px",
      border: "1px solid #d5d9e8",
      borderRadius: "8px",
      fontSize: "12px",
    },
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      contentStyle={{
        maxWidth: "900px",
        width: "90vw",
        ...(window.innerWidth <= 600
          ? { width: "98vw", minWidth: "98vw" }
          : {}),
      }}
      modal
      nested
    >
      <style>{`
        .edit-result-modal-content { padding: 16px; max-width: 900px; max-height: 80vh; overflow: auto; font-size: 0.9rem; }
        .edit-result-title { margin-bottom: 16px; text-align: center; font-size: 1.2rem; color: #121826; }
        .edit-result-label { display: block; margin-bottom: 6px; font-weight: 600; font-size: 0.85rem; color: #344054; }
        .edit-result-input { width: 100%; padding: 8px 10px; border: 1px solid #d5d9e8; border-radius: 8px; font-size: 0.84rem; }
        .edit-result-button { padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 0.78rem; }
        .edit-result-winner-item { display: grid; grid-template-columns: 48px minmax(0, 1fr) 188px; align-items: start; gap: 12px; padding: 12px 8px; border-bottom: 1px solid #eceff6; }
        .edit-result-winner-position { min-width: 42px; padding-top: 10px; font-weight: 700; color: #475467; }
        .edit-result-winner-name-block { flex: 1; min-width: 0; }
        .edit-result-winner-fields { display: grid; grid-template-columns: minmax(180px, 260px) minmax(220px, 1fr); align-items: center; gap: 8px; width: 100%; }
        .edit-result-user-id { font-weight: 700; color: #555; min-width: 190px; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .edit-result-name-input { flex: 1 1 220px; min-width: 180px; }
        .edit-result-action-col { display: flex; flex-direction: column; gap: 8px; border-left: 1px solid #eceff6; padding-left: 12px; align-self: stretch; justify-content: center; }
        .edit-result-action-btn { width: 100%; margin: 0 !important; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
        .edit-result-action-btn.is-delete { background: #dc3545 !important; color: #fff !important; border-color: #dc3545 !important; }
        .edit-result-add-winner { min-width: 0; }
        .edit-result-empty-label { color: #667085; font-weight: 600; font-size: 0.82rem; }
        .edit-result-secondary-fields { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 8px; }
        .edit-result-secondary-fields .edit-result-input { flex: 1 1 200px; min-width: 170px; }
        @media (max-width: 600px) {
          .edit-result-modal-content { padding: 8px; font-size: 0.82rem; min-width: 98vw; width: 98vw; }
          .edit-result-title { font-size: 0.98rem; }
          .edit-result-label { font-size: 0.8rem; }
          .edit-result-input { font-size: 0.82rem; padding: 6px; }
          .edit-result-button { font-size: 0.8rem; padding: 8px 0; width: 100%; margin-bottom: 6px; }
          .edit-result-winner-item { grid-template-columns: 1fr; align-items: stretch; padding: 6px 2px; font-size: 0.82rem; }
          .edit-result-winner-position { min-width: 0; padding-top: 0; }
          .edit-result-winner-name-block { width: 100%; }
          .edit-result-winner-fields { grid-template-columns: 1fr; align-items: stretch; gap: 6px; width: 100%; }
          .edit-result-user-id,
          .edit-result-name-input,
          .edit-result-action-btn,
          .edit-result-add-winner,
          .edit-result-secondary-fields .edit-result-input { width: 100%; min-width: 0; max-width: none; }
          .edit-result-action-col { border-left: 0; border-top: 1px solid #eceff6; padding-left: 0; padding-top: 8px; }
          .edit-result-secondary-fields { flex-direction: column; }
          .edit-result-button-group { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
        }
        .edit-result-search-form { display: flex; }
        @media (max-width: 600px) {
          .edit-result-search-form { flex-direction: column; gap: 8px; }
        }
        .edit-result-notification {
          padding: 12px;
          margin-bottom: 15px;
          border-radius: 4px;
          font-weight: 500;
          text-align: center;
          animation: slideDown 0.3s ease-out;
        }
        .edit-result-notification.error {
          background-color: #fee;
          color: #c33;
          border: 1px solid #fcc;
        }
        .edit-result-notification.success {
          background-color: #efe;
          color: #3c3;
          border: 1px solid #cfc;
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        /* ---- Result Image Section ---- */
        .result-image-section {
          border: 1px solid #d5d9e8;
          border-radius: 10px;
          padding: 16px;
          margin-bottom: 20px;
          background: #f8f9fe;
        }
        .result-image-section-title {
          font-size: 0.88rem;
          font-weight: 700;
          color: #344054;
          margin: 0 0 12px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .result-image-mode-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 20px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.04em;
        }
        .result-image-mode-badge.image-active {
          background: #d1fae5;
          color: #065f46;
        }
        .result-image-mode-badge.image-inactive {
          background: #f3f4f6;
          color: #6b7280;
        }
        .result-image-preview {
          width: 100%;
          max-height: 180px;
          object-fit: contain;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          margin-bottom: 12px;
          background: #fff;
        }
        .result-image-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }
        .result-image-upload-btn {
          padding: 7px 14px;
          border-radius: 8px;
          background: #4f46e5;
          color: #fff;
          border: none;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 700;
        }
        .result-image-upload-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .result-image-remove-btn {
          padding: 7px 14px;
          border-radius: 8px;
          background: #fff;
          color: #dc2626;
          border: 1px solid #fca5a5;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 700;
        }
        .result-image-remove-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .result-image-error {
          color: #dc2626;
          font-size: 0.78rem;
          margin-top: 6px;
        }
        .result-image-hint {
          color: #6b7280;
          font-size: 0.75rem;
          margin-top: 6px;
        }
        .result-image-tooltip-container {
          position: relative;
          display: inline-flex;
          align-items: center;
          cursor: help;
        }
        .result-image-tooltip-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background-color: #6b7280;
          color: #fff;
          font-size: 11px;
          font-weight: bold;
        }
        .result-image-tooltip-text {
          visibility: hidden;
          width: 320px;
          background-color: #374151;
          color: #f9fafb;
          text-align: left;
          border-radius: 6px;
          padding: 12px;
          position: absolute;
          z-index: 10;
          top: 125%;
          left: 50%;
          margin-left: -160px;
          opacity: 0;
          transition: opacity 0.2s;
          font-size: 13px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        .result-image-tooltip-text::after {
          content: "";
          position: absolute;
          bottom: 100%;
          left: 50%;
          margin-left: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: transparent transparent #374151 transparent;
        }
        .result-image-tooltip-container:hover .result-image-tooltip-text {
          visibility: visible;
          opacity: 1;
        }
        .result-image-tooltip-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 8px;
        }
        .result-image-tooltip-table th, .result-image-tooltip-table td {
          border: 1px solid #4b5563;
          padding: 6px;
        }
        .result-image-tooltip-table th {
          background-color: #1f2937;
          font-weight: 600;
        }
      `}</style>
      <div className="edit-result-modal-content">
        <h2 className="edit-result-title">Edit Contest Results</h2>
        {notificationMessage && (
          <div
            className={`edit-result-notification ${notificationType}`}
            style={{
              padding: "12px",
              marginBottom: "15px",
              borderRadius: "4px",
              fontWeight: "500",
              textAlign: "center",
              backgroundColor: notificationType === "error" ? "#fee" : "#efe",
              color: notificationType === "error" ? "#c33" : "#3c3",
              border: `1px solid ${
                notificationType === "error" ? "#fcc" : "#cfc"
              }`,
            }}
          >
            {notificationMessage}
          </div>
        )}

        {/* ---- Result Image Section ---- */}
        <div className="result-image-section">
          <div className="result-image-section-title" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
            <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151' }}>🖼️ Result Image</span>
            <div className="result-image-tooltip-container">
              <span className="result-image-tooltip-icon">?</span>
              <div className="result-image-tooltip-text">
                <table className="result-image-tooltip-table">
                  <thead>
                    <tr><th>Total Winners</th><th>Recommended Aspect Ratio</th><th>Example Height</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>1 to 3</td><td>~ 1:1 or 4:5</td><td>1200px - 1500px</td></tr>
                    <tr><td>4 to 6</td><td>~ 9:16 or 1:1.8</td><td>2133px - 2160px</td></tr>
                    <tr><td>10+</td><td>~ 1:3 or 1:4</td><td>3600px - 4800px</td></tr>
                  </tbody>
                </table>
                <div style={{ fontSize: '12px', color: '#d1d5db' }}>
                  * Width should be ~1200px. Height is variable.
                </div>
              </div>
            </div>
            {currentResultImage ? (
              <span className="result-image-mode-badge image-active">
                IMAGE MODE — dynamic cards hidden
              </span>
            ) : (
              <span className="result-image-mode-badge image-inactive">
                No image — showing dynamic cards
              </span>
            )}
          </div>
          {currentResultImage && (
            <img
              src={currentResultImage}
              alt="Contest result"
              className="result-image-preview"
            />
          )}
          <div className="result-image-actions">
            {/* Hidden file input */}
            <input
              ref={resultImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleResultImageUpload}
              id="result-image-file-input"
            />
            <button
              className="result-image-upload-btn"
              disabled={isImageUploading}
              onClick={() => resultImageInputRef.current && resultImageInputRef.current.click()}
              type="button"
            >
              {isImageUploading
                ? "Uploading..."
                : currentResultImage
                ? "Replace Image"
                : "Upload Result Image"}
            </button>
            {currentResultImage && (
              <button
                className="result-image-remove-btn"
                disabled={isImageUploading}
                onClick={handleResultImageRemove}
                type="button"
              >
                Remove Image
              </button>
            )}
          </div>
          {imageUploadError && (
            <p className="result-image-error">{imageUploadError}</p>
          )}
          <p className="result-image-hint">
            {currentResultImage
              ? "The uploaded image is shown on the frontend & app. Remove it to revert to the dynamic winner cards."
              : "Upload an image to replace the dynamic winner cards on the frontend & app."}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 10,
            marginBottom: 20,
          }}
        >
          <button
            onClick={() => setActiveTab("auto")}
            style={{
              ...modalStyle.button,
              ...(activeTab === "auto"
                ? modalStyle.applyButton
                : modalStyle.cancelButton),
              fontWeight: activeTab === "auto" ? "bold" : "normal",
              minWidth: 100,
            }}
          >
            Auto
          </button>
          <button
            onClick={() => setActiveTab("judge")}
            style={{
              ...modalStyle.button,
              ...(activeTab === "judge"
                ? modalStyle.applyButton
                : modalStyle.cancelButton),
              fontWeight: activeTab === "judge" ? "bold" : "normal",
              minWidth: 100,
            }}
          >
            Judge
          </button>
        </div>
        <div style={modalStyle.searchContainer}>
          <form
            onSubmit={handleSearch}
            className="edit-result-search-form"
            style={modalStyle.searchForm}
          >
            <input
              id="result-search-input"
              type="text"
              placeholder="Search participants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!isSearchActive}
              className="edit-result-input"
              style={{
                    marginRight: "10px",
                }}
            />
            <button
              type="submit"
              disabled={!isSearchActive}
              className="edit-result-button"
              style={modalStyle.searchButton}
            >
              Search
            </button>
          </form>
          <div style={modalStyle.searchResults}>
            {searchResults.length > 0 ? (
              searchResults.map((user) => (
                <div
                  key={user.id}
                  style={modalStyle.searchResultItem}
                  onClick={() => selectWinner(user.id, user.name)}
                >
                  {user.name} ({user.id})
                </div>
              ))
            ) : (
              <p>No results found</p>
            )}
          </div>
        </div>
        {loading ? (
          <p style={{ textAlign: "center" }}>Loading results...</p>
        ) : activeTab === "auto" && isLanguageBased ? (
          <div style={modalStyle.winnersList}>
            <h4>Current Results</h4>
            <div
              style={{
                marginBottom: "20px",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={displayAuto}
                  onChange={(e) => handleDisplayAutoChange(e.target.checked)}
                  style={{
                    marginRight: "8px",
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontWeight: "500" }}>Display Auto Result</span>
              </label>
              <label
                className="edit-result-label"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}
              >
                Auto Result Title:
              </label>
              <input
                type="text"
                value={currentResultTitle}
                onChange={(e) => setCurrentResultTitle(e.target.value)}
                placeholder="Enter title for auto results"
                className="edit-result-input"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
            {languages.map((languageId) => (
              <div key={languageId} style={modalStyle.languageSection}>
                <h3 style={modalStyle.languageTitle}>
                  {languageMap[languageId]
                    ? `${languageMap[languageId].name}`
                    : "Loading..."}
                </h3>
                {languageWinners[languageId]?.map((winner, index) => (
                  <div key={index} className="edit-result-winner-item">
                    <div className="edit-result-winner-position">#{winner.position}</div>
                    <div className="edit-result-winner-name-block" style={modalStyle.winnerName}>
                      <div className="edit-result-winner-fields">
                        {winner.userId && (
                          <span className="edit-result-user-id">
                            {winner.userId}
                          </span>
                        )}
                        <input
                          type="text"
                          value={
                            !winner.name || winner.name === "Empty Position"
                              ? ""
                              : winner.name
                          }
                          readOnly={!!winner.userId}
                          disabled={!!winner.userId}
                          placeholder="Name"
                          className="edit-result-input edit-result-name-input"
                          style={{
                            ...modalStyle.inputField,
                            backgroundColor: winner.userId
                              ? "#f0f0f0"
                              : "white",
                            color: winner.userId ? "#888" : "black",
                            minWidth: 120,
                          }}
                          onChange={(e) =>
                            !winner.userId &&
                            handleNameChange(languageId, index, e.target.value)
                          }
                        />
                      </div>
                      <div className="edit-result-secondary-fields">
                        <input
                          type="text"
                          placeholder="Tag (e.g., Winner, Runner Up)"
                          value={winner.tag}
                          onChange={(e) =>
                            handleTagChange(languageId, index, e.target.value)
                          }
                          className="edit-result-input"
                          style={modalStyle.inputField}
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="Prize Money"
                          value={winner.prizeMoney}
                          onChange={(e) =>
                            handlePrizeMoneyChange(
                              languageId,
                              index,
                              e.target.value
                            )
                          }
                          className="edit-result-input"
                          style={modalStyle.inputField}
                        />
                      </div>
                    </div>
                    <div className="edit-result-action-col">
                      {winner.userId ? (
                        <>
                          <button
                            style={modalStyle.editButton}
                            className="edit-result-button edit-result-action-btn"
                            onClick={() => handleEditClick(index, languageId)}
                          >
                            <FontAwesomeIcon
                              icon={faEdit}
                              style={{ fontSize: "12px" }}
                            />
                            Edit
                          </button>
                          <button
                            style={modalStyle.editButton}
                            className="edit-result-button edit-result-action-btn is-delete"
                            onClick={() => handleClearPosition(index, languageId)}
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{ fontSize: "12px" }}
                            />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            style={modalStyle.addButton}
                            className="edit-result-button edit-result-action-btn edit-result-add-winner"
                            onClick={() => handleAddWinner(index, languageId)}
                          >
                            <FontAwesomeIcon
                              icon={faPlus}
                              style={{ fontSize: "12px" }}
                            />
                            Add Winner
                          </button>
                          <button
                            style={modalStyle.editButton}
                            className="edit-result-button edit-result-action-btn is-delete"
                            onClick={() => handleClearPosition(index, languageId)}
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{ fontSize: "12px" }}
                            />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : activeTab === "auto" ? (
          <div style={modalStyle.winnersList}>
            <h4>Current Results</h4>
            <div
              style={{
                marginBottom: "20px",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={displayAuto}
                  onChange={(e) => handleDisplayAutoChange(e.target.checked)}
                  style={{
                    marginRight: "8px",
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontWeight: "500" }}>Display Auto Result</span>
              </label>
              <label
                className="edit-result-label"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}
              >
                Auto Result Title:
              </label>
              <input
                type="text"
                value={currentResultTitle}
                onChange={(e) => setCurrentResultTitle(e.target.value)}
                placeholder="Enter title for auto results"
                className="edit-result-input"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
            {winnersList.map((winner, index) => (
              <div key={index} className="edit-result-winner-item">
                <div className="edit-result-winner-position">#{winner.position}</div>
                <div className="edit-result-winner-name-block" style={modalStyle.winnerName}>
                  <div className="edit-result-winner-fields">
                    {winner.userId ? (
                      <>
                        <span className="edit-result-user-id">
                          {winner.name} ({winner.userId})
                        </span>
                      </>
                    ) : (
                      <span className="edit-result-empty-label">Empty Position</span>
                    )}
                  </div>
                </div>
                <div className="edit-result-action-col">
                  {winner.userId ? (
                    <>
                      <button
                        style={modalStyle.editButton}
                        onClick={() => handleEditClick(index)}
                        className="edit-result-button edit-result-action-btn"
                      >
                        <FontAwesomeIcon
                          icon={faEdit}
                          style={{ fontSize: "12px" }}
                        />
                        Edit
                      </button>
                      <button
                        style={modalStyle.editButton}
                        onClick={() => handleClearPosition(index)}
                        className="edit-result-button edit-result-action-btn is-delete"
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          style={{ fontSize: "12px" }}
                        />
                      </button>
                    </>
                  ) : (
                    <button
                      style={modalStyle.addButton}
                      onClick={() => handleAddWinner(index)}
                      className="edit-result-button edit-result-action-btn edit-result-add-winner"
                    >
                      <FontAwesomeIcon
                        icon={faPlus}
                        style={{ fontSize: "12px" }}
                      />
                      Add Winner
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={modalStyle.winnersList}>
            <h4>Judge Results</h4>
            <div
              style={{
                marginBottom: "20px",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={displayJudge}
                  onChange={(e) => handleDisplayJudgeChange(e.target.checked)}
                  style={{
                    marginRight: "8px",
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontWeight: "500" }}>Display Judge Result</span>
              </label>
              <label
                className="edit-result-label"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}
              >
                Judge Result Title:
              </label>
              <input
                type="text"
                value={currentJudgeResultTitle}
                onChange={(e) => setCurrentJudgeResultTitle(e.target.value)}
                placeholder="Enter title for judge results"
                className="edit-result-input"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
            {winnersList.map((winner, index) => (
              <div key={index} className="edit-result-winner-item">
                <div className="edit-result-winner-position">#{winner.position}</div>
                <div className="edit-result-winner-name-block" style={modalStyle.winnerName}>
                  <div className="edit-result-winner-fields">
                    {winner.userId && (
                      <span className="edit-result-user-id">
                        {winner.userId}
                      </span>
                    )}
                    <input
                      type="text"
                      value={winner.name || ""}
                      readOnly={!!winner.userId}
                      disabled={!!winner.userId}
                      placeholder="Name"
                      className="edit-result-input edit-result-name-input"
                      style={{
                        ...modalStyle.inputField,
                        backgroundColor: winner.userId ? "#f0f0f0" : "white",
                        color: winner.userId ? "#888" : "black",
                        minWidth: 120,
                      }}
                      onChange={(e) =>
                        !winner.userId &&
                        handleNameChange(null, index, e.target.value)
                      }
                    />
                  </div>
                  <div className="edit-result-secondary-fields">
                    <input
                      type="text"
                      placeholder="Tag (e.g., Winner, Runner Up)"
                      value={winner.tag}
                      onChange={(e) =>
                        handleTagChange(null, index, e.target.value)
                      }
                      className="edit-result-input"
                      style={modalStyle.inputField}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Prize Money"
                      value={winner.prizeMoney}
                      onChange={(e) =>
                        handlePrizeMoneyChange(null, index, e.target.value)
                      }
                      className="edit-result-input"
                      style={modalStyle.inputField}
                    />
                  </div>
                </div>
                <div className="edit-result-action-col">
                  {winner.userId ? (
                    <>
                      <button
                        style={modalStyle.editButton}
                        onClick={() => handleEditClick(index)}
                        className="edit-result-button edit-result-action-btn"
                      >
                        <FontAwesomeIcon
                          icon={faEdit}
                          style={{ fontSize: "12px" }}
                        />
                        Edit
                      </button>
                      <button
                        style={modalStyle.editButton}
                        onClick={() => handleClearPosition(index)}
                        className="edit-result-button edit-result-action-btn is-delete"
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          style={{ fontSize: "12px" }}
                        />
                      </button>
                    </>
                  ) : (
                    <button
                      style={modalStyle.addButton}
                      onClick={() => handleAddWinner(index)}
                      className="edit-result-button edit-result-action-btn edit-result-add-winner"
                    >
                      <FontAwesomeIcon
                        icon={faPlus}
                        style={{ fontSize: "12px" }}
                      />
                      Add Winner
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div style={{ marginTop: "15px", textAlign: "center" }}>
              <button
                onClick={handleAddJudgeRow}
                style={{
                  ...modalStyle.addButton,
                  padding: "10px 20px",
                  fontSize: "14px",
                }}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  style={{ fontSize: "14px", marginRight: "8px" }}
                />
                Add New Row
              </button>
            </div>
          </div>
        )}

        <div
          className={
            window.innerWidth <= 600 ? "edit-result-button-group" : undefined
          }
          style={modalStyle.buttonGroup}
        >
          <button
            onClick={onClose}
            className="edit-result-button"
            style={{ ...modalStyle.button, ...modalStyle.cancelButton }}
          >
            Cancel
          </button>
          <button
            onClick={handleResetAll}
            className="edit-result-button"
            style={{
              ...modalStyle.button,
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
            }}
          >
            Reset All
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="edit-result-button"
            style={{ ...modalStyle.button, ...modalStyle.applyButton }}
          >
            {isSubmitting ? "Updating..." : "Apply Changes"}
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default EditResult;
